import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import { TextStyle } from '@tiptap/extension-text-style';
import { Bold, Highlighter, Type } from 'lucide-react';

// Custom extension to handle font sizes
const FontSize = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fontSize: {
        default: null,
        parseHTML: element => element.style.fontSize?.replace('px', ''),
        renderHTML: attributes => {
          if (!attributes.fontSize) return {};
          return { style: `font-size: ${attributes.fontSize}px` };
        },
      },
    };
  },
});

// Toolbar Button Component
function ToolbarButton({ onClick, active, disabled, children, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-2 rounded hover:bg-slate-200 transition-colors ${
        active ? 'bg-slate-200 text-blue-600' : 'text-slate-600'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {children}
    </button>
  );
}

export default function RichTextEditor({ 
  value, 
  onChange, 
  placeholder = "Enter notes...",
  disabled = false,
  className = ""
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
      }),
      FontSize,
      Highlight.configure({ multicolor: true }),
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[100px] p-3',
        placeholder: placeholder,
      },
    },
  });

  // Update editor content when value changes externally
  if (editor && value !== editor.getHTML() && value !== undefined) {
    const currentHTML = editor.getHTML();
    if (value !== currentHTML && !(value === '' && currentHTML === '<p></p>')) {
      editor.commands.setContent(value || '');
    }
  }

  if (!editor) {
    return null;
  }

  const setFontSize = (size) => {
    if (size === 'normal') {
      editor.chain().focus().unsetMark('textStyle').run();
    } else {
      editor.chain().focus().setMark('textStyle', { fontSize: size }).run();
    }
  };

  const toggleHighlight = (color) => {
    if (editor.isActive('highlight')) {
      editor.chain().focus().unsetHighlight().run();
    } else {
      editor.chain().focus().toggleHighlight({ color }).run();
    }
  };

  return (
    <div className={`rich-text-editor border border-slate-200 rounded-lg overflow-hidden ${className}`} data-testid="rich-text-editor">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 bg-slate-50 border-b border-slate-200">
        {/* Font Size Dropdown */}
        <div className="relative">
          <select
            onChange={(e) => setFontSize(e.target.value)}
            className="h-8 px-2 pr-6 text-sm border border-slate-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            title="Font Size"
          >
            <option value="12">Small</option>
            <option value="normal" selected>Normal</option>
            <option value="18">Large</option>
          </select>
        </div>

        <div className="w-px h-6 bg-slate-300 mx-1" />

        {/* Bold */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          disabled={disabled}
          title="Bold (Ctrl+B)"
        >
          <Bold className="h-4 w-4" />
        </ToolbarButton>

        <div className="w-px h-6 bg-slate-300 mx-1" />

        {/* Highlight Colors */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-slate-500 mr-1">Highlight:</span>
          {[
            { color: '#ffff00', label: 'Yellow' },
            { color: '#00ff00', label: 'Green' },
            { color: '#00ffff', label: 'Cyan' },
            { color: '#ff00ff', label: 'Magenta' },
          ].map(({ color, label }) => (
            <button
              key={color}
              type="button"
              onClick={() => toggleHighlight(color)}
              className={`w-6 h-6 rounded border-2 ${
                editor.isActive('highlight', { color }) 
                  ? 'border-blue-500' 
                  : 'border-slate-300'
              }`}
              style={{ backgroundColor: color }}
              title={`Highlight ${label}`}
            />
          ))}
          <button
            type="button"
            onClick={() => editor.chain().focus().unsetHighlight().run()}
            className="h-6 px-2 text-xs border border-slate-300 rounded hover:bg-slate-100"
            title="Remove Highlight"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Editor Content */}
      <EditorContent 
        editor={editor} 
        className="bg-white"
      />

      <style>{`
        .ProseMirror {
          min-height: 100px;
          padding: 12px;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          color: #94a3b8;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .ProseMirror:focus {
          outline: none;
        }
        .ProseMirror mark {
          padding: 0 2px;
        }
      `}</style>
    </div>
  );
}

